import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowDownToLine, ArrowRightLeft, ArrowUpFromLine, BadgeCheck, ExternalLink, Search as SearchIcon, X } from "lucide-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { WarehouseOrderDirection, WarehouseOrderKind, WarehouseOrderRow, WarehouseOrderScope } from "@patronix/domain/warehouse-orders";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import type { ListTools } from "@/components/ListToolbar";
import { PanelFrame, PanelSkeleton } from "@/components/Panel";
import { Badge, type StatusTone } from "@/components/ui";
import {
  listWarehouseOrders,
  sourceLink,
  warehouseOrderLinesQuery,
  warehouseOrderListQuery,
  type WarehouseOrderListParams,
} from "@/features/warehouse-orders/api";
import { warehouseListQuery } from "@/features/warehousing/api";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { sessionContextQuery } from "@/lib/session";

// Warehousing → Warehouse orders (PX-WHS-120): everything the warehouse has to
// execute, from every origin — purchase receipts, sales shipments, material
// issue and finished goods receipt of production orders, transfers and
// inspections — derived from the origin documents (read only; postings stay
// on their own screens). Keyset on (planned date, origin, id) through
// warehouse_order_page; the lines of one order in the detail, one RPC,
// prefetched on hover. Computed list: export only, no import (ADR-034).

interface Search {
  scope: WarehouseOrderScope;
  dir?: WarehouseOrderDirection | undefined;
  wh?: string | undefined;
  q: string;
  kind?: WarehouseOrderKind | undefined;
  id?: string | undefined;
}

// Local lists (not imported from @patronix/domain: that module carries zod,
// and validateSearch is in the entry chunk).
const warehouseOrderScopes = ["open", "done", "all"] as const satisfies readonly WarehouseOrderScope[];
const warehouseOrderDirections = ["inbound", "outbound", "transfer", "quality"] as const satisfies readonly WarehouseOrderDirection[];
const warehouseOrderKinds = [
  "purchase_receipt",
  "sales_shipment",
  "production_issue",
  "production_receipt",
  "transfer",
  "inspection",
] as const satisfies readonly WarehouseOrderKind[];
const isOneOf = <T extends string>(list: readonly T[], v: unknown): v is T => typeof v === "string" && (list as readonly string[]).includes(v);

export const Route = createFileRoute("/_app/warehousing/warehouse-orders")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      scope: isOneOf(warehouseOrderScopes, raw.scope) ? raw.scope : "open",
      q: typeof raw.q === "string" ? raw.q.slice(0, 40) : "",
    };
    if (isOneOf(warehouseOrderDirections, raw.dir)) s.dir = raw.dir;
    if (typeof raw.wh === "string" && raw.wh.length <= 64) s.wh = raw.wh;
    if (isOneOf(warehouseOrderKinds, raw.kind) && typeof raw.id === "string" && raw.id && raw.id.length <= 64) {
      s.kind = raw.kind;
      s.id = raw.id;
    }
    return s;
  },
  loaderDeps: ({ search }) => ({ scope: search.scope, dir: search.dir, wh: search.wh, q: search.q }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId)
      await context.queryClient.ensureInfiniteQueryData(
        warehouseOrderListQuery({ companyId, scope: deps.scope, direction: deps.dir ?? null, q: deps.q, warehouseId: deps.wh ?? null }),
      );
  },
  component: WarehouseOrdersPage,
});

const directionIcon: Record<WarehouseOrderDirection, ReactNode> = {
  inbound: <ArrowDownToLine size={14} aria-hidden="true" />,
  outbound: <ArrowUpFromLine size={14} aria-hidden="true" />,
  transfer: <ArrowRightLeft size={14} aria-hidden="true" />,
  quality: <BadgeCheck size={14} aria-hidden="true" />,
};

function statusTone(status: string, isOpen: boolean): StatusTone {
  if (!isOpen) return "success";
  if (status === "partially_received" || status === "partially_shipped" || status === "in_progress") return "warning";
  if (status === "draft" || status === "open") return "neutral";
  return "info";
}

function WarehouseOrdersPage() {
  const { t } = useTranslation();
  const tr = t as unknown as (key: string, o?: Record<string, unknown>) => string;
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { company } = useActiveCompany();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params: WarehouseOrderListParams = {
    companyId: company?.id ?? "",
    scope: search.scope,
    direction: search.dir ?? null,
    q: search.q,
    warehouseId: search.wh ?? null,
  };
  const query = useInfiniteQuery(warehouseOrderListQuery(params));
  const warehouses = useInfiniteQuery(warehouseListQuery(false));
  const whOptions = (warehouses.data?.pages.flatMap((p) => p.rows) ?? []).filter((w) => !company || w.company_id === company.id);
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, kind: undefined, id: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const columns: Column<WarehouseOrderRow>[] = [
    {
      key: "number",
      header: t("wo.colNumber"),
      width: "minmax(130px,0.9fr)",
      cell: (r, selected) => (
        <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>
          {r.number ?? <span className="font-sans italic text-ink-muted">{t("wo.draftTransfer")}</span>}
        </span>
      ),
    },
    {
      key: "kind",
      header: t("wo.colKind"),
      width: "minmax(170px,1fr)",
      cell: (r) => (
        <span className="flex items-center gap-1.5 truncate text-ink-soft">
          {directionIcon[r.direction]}
          {tr(`wo.kind.${r.kind}`)}
        </span>
      ),
    },
    {
      key: "date",
      header: t("wo.colDate"),
      width: "minmax(100px,0.6fr)",
      className: "tabular text-ink-soft",
      cell: (r) => fmt.day(r.planned_date),
    },
    { key: "warehouse", header: t("wo.colWarehouse"), width: "minmax(90px,0.5fr)", cell: (r) => <span className="font-mono text-xs">{r.warehouse_code ?? "—"}</span> },
    { key: "party", header: t("wo.colParty"), width: "minmax(180px,1.5fr)", cell: (r) => <span className="truncate">{r.party ?? "—"}</span> },
    { key: "lines", header: t("wo.colLines"), width: "minmax(60px,0.4fr)", align: "end", cell: (r) => <span className="tabular">{r.line_count}</span> },
    {
      key: "status",
      header: t("wo.colStatus"),
      width: "minmax(130px,0.8fr)",
      cell: (r) => <Badge tone={statusTone(r.status, r.is_open)}>{tr(`status.${r.status}`)}</Badge>,
    },
  ];
  const exportColumns: ExportColumn<WarehouseOrderRow>[] = [
    { header: t("wo.colNumber"), kind: "code", value: (r) => r.number },
    { header: "kind", kind: "code", value: (r) => r.kind },
    { header: "direction", kind: "code", value: (r) => r.direction },
    { header: t("wo.colDate"), kind: "date", value: (r) => r.planned_date },
    { header: t("wo.colWarehouse"), kind: "code", value: (r) => r.warehouse_code },
    { header: t("wo.colParty"), kind: "text", value: (r) => r.party },
    { header: t("wo.colLines"), kind: "integer", value: (r) => r.line_count },
    { header: "status", kind: "code", value: (r) => r.status },
  ];
  const listTools: ListTools<WarehouseOrderRow> = {
    id: "warehouse-orders",
    title: t("wo.title"),
    export: { fileBase: "warehouse-orders", sheet: t("wo.title"), columns: exportColumns, fetchPage: (cursor, signal) => listWarehouseOrders(params, cursor, signal, EXPORT_PAGE) },
  };
  const chip = (pressed: boolean) =>
    cn(
      "h-8 rounded-full border px-3 text-[13px] transition-colors",
      pressed ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
    );

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.warehousing.title")} <span aria-hidden="true">/</span> {t("nav.warehousing.warehouseOrders")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("wo.title")}</h1>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("wo.colStatus")} className="flex flex-wrap gap-2">
          {warehouseOrderScopes.map((s) => (
            <button key={s} type="button" aria-pressed={search.scope === s} onClick={() => setSearch({ scope: s, kind: undefined, id: undefined })} className={chip(search.scope === s)}>
              {tr(`wo.scope.${s}`)}
            </button>
          ))}
        </div>
        <div role="group" aria-label={t("wo.colKind")} className="flex flex-wrap gap-2">
          <button type="button" aria-pressed={!search.dir} onClick={() => setSearch({ dir: undefined, kind: undefined, id: undefined })} className={chip(!search.dir)}>
            {t("wo.direction.all")}
          </button>
          {warehouseOrderDirections.map((d) => (
            <button key={d} type="button" aria-pressed={search.dir === d} onClick={() => setSearch({ dir: d, kind: undefined, id: undefined })} className={cn(chip(search.dir === d), "inline-flex items-center gap-1.5")}>
              {directionIcon[d]}
              {tr(`wo.direction.${d}`)}
            </button>
          ))}
        </div>
        <div className="grow" />
        <select
          aria-label={t("wo.colWarehouse")}
          value={search.wh ?? ""}
          onChange={(e) => setSearch({ wh: e.target.value || undefined, kind: undefined, id: undefined })}
          className="h-9 rounded-xl border border-border bg-surface px-3 text-[13px]"
        >
          <option value="">{t("wo.allWarehouses")}</option>
          {whOptions.map((w) => (
            <option key={w.id} value={w.id}>
              {w.code} · {w.name}
            </option>
          ))}
        </select>
        <label className="flex h-9 min-w-[200px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
          <SearchIcon size={15} aria-hidden="true" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("wo.searchPlaceholder")}
            aria-label={t("wo.searchPlaceholder")}
            data-list-search
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {text && (
            <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
              <X size={14} />
            </button>
          )}
        </label>
      </div>

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("wo.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => `${r.kind}:${r.id}`}
          selectedId={search.kind && search.id ? `${search.kind}:${search.id}` : undefined}
          onSelect={(r) => setSearch({ kind: r.kind, id: r.id })}
          onClear={() => setSearch({ kind: undefined, id: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(warehouseOrderLinesQuery(r.kind, r.id))}
          emptyTitle={t("wo.emptyTitle")}
          emptyBody={t("wo.emptyBody")}
          minWidth={900}
          tools={listTools}
        />
        <DetailPanel
          label={t("wo.detailLabel")}
          open={!!search.id}
          placeholder={<DetailPlaceholder title={t("wo.selectPrompt")} hint={t("wo.selectHint")} />}
        >
          {search.kind && search.id && (
            <WarehouseOrderPanel
              key={`${search.kind}:${search.id}`}
              kind={search.kind}
              id={search.id}
              row={query.data?.pages.flatMap((p) => p.rows).find((r) => r.kind === search.kind && r.id === search.id)}
              onClose={() => setSearch({ kind: undefined, id: undefined })}
            />
          )}
        </DetailPanel>
      </div>
    </div>
  );
}

function WarehouseOrderPanel({
  kind,
  id,
  row,
  onClose,
}: {
  kind: WarehouseOrderKind;
  id: string;
  row: WarehouseOrderRow | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const tr = t as unknown as (key: string, o?: Record<string, unknown>) => string;
  const fmt = useFormat();
  const { data: lines, isPending, error } = useQuery(warehouseOrderLinesQuery(kind, id));
  const link = sourceLink(kind, id);
  const isTransfer = kind === "transfer";

  return (
    <PanelFrame
      eyebrow={tr(`wo.kind.${kind}`)}
      title={row?.number ?? (isTransfer ? t("wo.draftTransfer") : "—")}
      badge={row ? <Badge tone={statusTone(row.status, row.is_open)}>{tr(`status.${row.status}`)}</Badge> : undefined}
      onClose={onClose}
    >
      <div className="flex flex-col gap-1 text-[13px]">
        {row?.party && <span>{row.party}</span>}
        <span className="text-ink-muted">{t("wo.hint")}</span>
        <Link to={link.to as never} search={link.search as never} className="inline-flex w-fit items-center gap-1.5 font-medium text-accent-ink hover:underline">
          <ExternalLink size={14} aria-hidden="true" />
          {tr("wo.openSource", { kind: tr(`wo.kind.${kind}`).toLocaleLowerCase() })}
        </Link>
      </div>
      <section className="flex flex-col gap-2">
        <h3 className="text-[13px] font-semibold">{t("wo.lines")}</h3>
        {isPending ? (
          <PanelSkeleton />
        ) : error ? (
          <p className="text-[13px] text-(--status-danger-fg)">{String((error as Error).message ?? error)}</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="text-left text-ink-muted">
              <tr>
                <th className="py-1 pr-2 font-medium">{t("wo.colItem")}</th>
                <th className="py-1 pr-2 text-right font-medium">{t("wo.colQty")}</th>
                <th className="py-1 pr-2 text-right font-medium">{t("wo.colDone")}</th>
                <th className="py-1 pr-2 font-medium">{t("wo.colFrom")}</th>
                {isTransfer && <th className="py-1 font-medium">{t("wo.colTo")}</th>}
              </tr>
            </thead>
            <tbody>
              {(lines ?? []).map((l) => (
                <tr key={l.line_no} className="border-t border-border align-top">
                  <td className="py-1.5 pr-2">
                    <span className="font-mono text-xs">{l.item_code}</span> <span className="text-ink-soft">{l.item_description}</span>
                  </td>
                  <td className="tabular py-1.5 pr-2 text-right">
                    {fmt.qty(String(l.qty))} {l.uom_code}
                  </td>
                  <td className={cn("tabular py-1.5 pr-2 text-right", Number(l.done_qty) >= Number(l.qty) && "text-(--status-success-fg)")}>{fmt.qty(String(l.done_qty))}</td>
                  <td className="py-1.5 pr-2 font-mono text-xs">{[l.warehouse_code, l.location_code].filter(Boolean).join(" · ") || "—"}</td>
                  {isTransfer && <td className="py-1.5 font-mono text-xs">{[l.to_warehouse_code, l.to_location_code].filter(Boolean).join(" · ")}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </PanelFrame>
  );
}
